import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import { Clock, Users, CheckCircle, AlertCircle, MessageSquare, BarChart3, FileText, Plus, Send, ThumbsUp, ThumbsDown, User, Settings, LogOut, ChevronRight, ChevronDown, ChevronUp, X, Mic, MicOff, Mail, Search, RefreshCw, Trash2, XCircle, ArrowLeft, Loader2, Bell, BellOff, Eye, Download, Filter, Columns, ArrowUpDown, Maximize2, Minimize2, ChevronLeft, ShieldAlert, Heart, Phone } from 'lucide-react';
import { useToast, useCrisisAlert, ToastProvider, CrisisContext } from '../shared.jsx';
import IdentifyDefineStep from './IdentifyDefineStep.jsx';
import CommunicateStep from './CommunicateStep.jsx';
import ExploreAlternativesStep from './ExploreAlternativesStep.jsx';
import EvaluateSelectStep from './EvaluateSelectStep.jsx';
import AgreeImplementStep from './AgreeImplementStep.jsx';
import FollowUpStep from './FollowUpStep.jsx';
import { storage, addAuditEntry, calculateWorkingHours, isWithinResponseHours, OWL_LOGO, EMAIL_CONFIG, detectHarmContent, notifyMentorOfHarmDetection, sendNotification, VoiceInputButton, useVoiceInput, initFCM, removeFCMToken, notificationPermissionGranted, requestNotificationPermission, CrisisResourceModal, getUrlParams, clearUrlParams, initFirebase, logAnalyticsEvent, deriveConflictAccess, useRelationships } from '../shared.jsx';
import { useBilling } from '../modules/billing/BillingProvider.jsx';

function ConflictProcess({ conflict, user, canEdit, onUpdate, onLogout, forceStep, onStepForced, onReturnToOverview }) {
  const [activeStep, setActiveStep] = useState(getCurrentStep(conflict));
  const { relationships } = useRelationships();
  const { billingReadOnly } = useBilling();

  // Handle forced step navigation from overview
  React.useEffect(() => {
    if (forceStep) {
      setActiveStep(forceStep);
      if (onStepForced) onStepForced();
    }
  }, [forceStep]);

  // Auto-update activeStep when conflict status changes (e.g., after rejection moves to communicate)
  React.useEffect(() => {
    const currentCorrectStep = getCurrentStep(conflict);
    // Only auto-navigate forward (don't go backwards if user is browsing history)
    const stepOrder = ['identify-define', 'communicate', 'explore-alternatives', 'evaluate-select', 'agree-implement', 'follow-up'];
    const currentIdx = stepOrder.indexOf(activeStep);
    const correctIdx = stepOrder.indexOf(currentCorrectStep);
    if (correctIdx > currentIdx) {
      // When transitioning to communicate with no cycles yet,
      // only the first speaker (rejector) should land on the step.
      // The other user should go back to overview since they have no action.
      if (currentCorrectStep === 'communicate' && (!conflict.steps?.communicate?.cycles || conflict.steps.communicate.cycles.length === 0)) {
        const skipVotes = conflict.steps?.identifyDefine?.skipCommunicationVotes || {};
        const noVoter = Object.values(skipVotes).find(v => !v.skip);
        const rejectorId = noVoter?.userId || conflict.updatedBy || null;
        if (rejectorId && rejectorId !== user.id) {
          // This user is NOT the first speaker — send them to overview
          if (onReturnToOverview) onReturnToOverview();
          return;
        }
      }
      setActiveStep(currentCorrectStep);
      logAnalyticsEvent('phase_transition', { 
        conflict_id: conflict.id, 
        phase: currentCorrectStep,
        from_phase: activeStep 
      });
    }
  }, [conflict.status]);

  function getCurrentStep(c) {
    if (c.status === 'resolved') return 'follow-up';
    if (c.status === 'follow-up') return 'follow-up';
    
    // If status is agree-implement but no ratings exist, should be in evaluate-select
    if (c.status === 'agree-implement') {
      const hasRatings = c.steps?.evaluateSelect?.ratings && 
                        Object.keys(c.steps.evaluateSelect.ratings).length > 0;
      if (!hasRatings) return 'evaluate-select';
      return 'agree-implement';
    }
    
    if (c.status === 'evaluate-select') return 'evaluate-select';
    if (c.status === 'explore-alternatives') return 'explore-alternatives';
    if (c.status === 'communicate') return 'communicate';
    if (c.status === 'identify-define') return 'identify-define';
    // Handle legacy conflicts with 'created' status
    if (c.status === 'created') return 'identify-define';
    // Default to first step
    return 'identify-define';
  }

  // Override canEdit to include creators and email-matched users
  // CRITICAL: Once a conflict is resolved, nobody can edit — everything is read-only
  // Access is now derived from the relationship chain, not just conflict membership
  const isResolved = conflict.status === 'resolved';
  const access = deriveConflictAccess(conflict, user, relationships);
  const userCanEdit = !isResolved && access.canWrite && !billingReadOnly?.readOnly;

  const steps = [
    { id: 'identify-define', label: 'Identify & Define', icon: FileText },
    { id: 'communicate', label: 'Communicate', icon: MessageSquare },
    { id: 'explore-alternatives', label: 'Explore Alternatives', icon: BarChart3 },
    { id: 'evaluate-select', label: 'Evaluate & Select', icon: CheckCircle },
    { id: 'agree-implement', label: 'Agree & Implement', icon: ThumbsUp },
    { id: 'follow-up', label: 'Follow-up', icon: Users }
  ];

  // Only show steps that are completed or the current active step (hide future steps)
  const currentStepId = getCurrentStep(conflict);
  const currentStepIdx = steps.findIndex(s => s.id === currentStepId);
  const visibleSteps = steps.filter((step, idx) => {
    const stepKey = step.id.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
    const isCompleted = conflict.steps[stepKey]?.completed;
    const isCurrent = idx <= currentStepIdx;
    return isCompleted || isCurrent;
  });

  return (
    <div>
      {/* Step Navigator */}
      <div className="mb-8 overflow-x-auto">
        <div className="flex items-center gap-2 min-w-max">
          {visibleSteps.map((step, idx) => {
            const Icon = step.icon;
            const isActive = step.id === activeStep;
            const stepKey = step.id.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
            const isCompleted = conflict.steps[stepKey]?.completed || idx < currentStepIdx;
            
            return (
              <React.Fragment key={step.id}>
                <button
                  aria-pressed={isActive}
                  onClick={() => { if (!isActive || visibleSteps.length > 1) setActiveStep(step.id); }}
                  className={`flex items-center gap-2 px-4 py-3 rounded-lg font-medium transition-all ${
                    isActive
                      ? visibleSteps.length === 1
                        ? 'bg-amber-700 text-white shadow-lg cursor-default'
                        : 'bg-amber-700 text-white shadow-lg'
                      : isCompleted
                      ? 'bg-green-100 text-green-700 hover:bg-green-200'
                      : 'bg-stone-100 text-stone-600 hover:bg-stone-200'
                  }`}
                >
                  <Icon className="w-5 h-5" />
                  <span className="whitespace-nowrap">{step.label}</span>
                  {isCompleted && <CheckCircle className="w-4 h-4" />}
                </button>
                {idx < visibleSteps.length - 1 && (
                  <ChevronRight className="w-5 h-5 text-stone-500 flex-shrink-0" />
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Step Content */}
      {isResolved && (
        <div className="mb-4 p-4 bg-green-50 rounded-xl border border-green-200 flex items-center gap-3">
          <CheckCircle className="w-6 h-6 text-green-700 flex-shrink-0" />
          <div>
            <div className="font-medium text-green-800">This conflict has been resolved</div>
            <p className="text-sm text-green-700">All steps below are read-only for your records.</p>
          </div>
        </div>
      )}
      {(() => {
        // === RESPONSE HOURS ENFORCEMENT ===
        // Hard block: if current time is outside the agreed engagement window,
        // replace all step content with a "come back later" message.
        // Only applies to active (non-resolved) conflicts.
        const hoursCheck = isWithinResponseHours(conflict);
        if (!isResolved && !hoursCheck.allowed) {
          const formatHour = (h) => {
            if (h === 0 || h === 24) return '12:00 AM';
            if (h === 12) return '12:00 PM';
            return h > 12 ? `${h - 12}:00 PM` : `${h}:00 AM`;
          };
          const hoursFromNow = Math.floor(hoursCheck.minutesUntilOpen / 60);
          const minsFromNow = hoursCheck.minutesUntilOpen % 60;
          return (
            <div className="bg-stone-50 rounded-xl p-3 sm:p-6">
              <div className="max-w-md mx-auto text-center py-12">
                <div className="w-16 h-16 bg-amber-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Clock className="w-8 h-8 text-amber-700" />
                </div>
                <h4 className="text-xl font-medium text-stone-800 mb-3">Outside Engagement Hours</h4>
                <p className="text-stone-600 mb-4">
                  Both parties agreed to engage with this conflict between{' '}
                  <strong>{formatHour(hoursCheck.startHour)}</strong> and{' '}
                  <strong>{formatHour(hoursCheck.endHour)}</strong>.
                </p>
                <p className="text-stone-600 mb-6">
                  It's currently <strong>{formatHour(hoursCheck.currentHour)}</strong>.
                  This step will become available in approximately{' '}
                  <strong>{hoursFromNow > 0 ? `${hoursFromNow}h ` : ''}{minsFromNow}m</strong>.
                </p>
                <div className="p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <p className="text-sm text-amber-800">
                    Respecting agreed-upon engagement hours is part of the structured resolution process.
                    It ensures both parties have time to reflect and respond thoughtfully.
                  </p>
                </div>
              </div>
            </div>
          );
        }
        // Within hours — render normal step content
        return (
          <div className="bg-stone-50 rounded-xl p-3 sm:p-6">
            {activeStep === 'identify-define' && (
              <IdentifyDefineStep conflict={conflict} user={user} canEdit={userCanEdit} onUpdate={onUpdate} onReturnToOverview={onReturnToOverview} />
            )}
            {activeStep === 'communicate' && (
              <CommunicateStep conflict={conflict} user={user} canEdit={userCanEdit} onUpdate={onUpdate} onReturnToOverview={onReturnToOverview} />
            )}
            {activeStep === 'explore-alternatives' && (
              <ExploreAlternativesStep conflict={conflict} user={user} canEdit={userCanEdit} onUpdate={onUpdate} onReturnToOverview={onReturnToOverview} />
            )}
            {activeStep === 'evaluate-select' && (
              <EvaluateSelectStep conflict={conflict} user={user} canEdit={userCanEdit} onUpdate={onUpdate} onReturnToOverview={onReturnToOverview} />
            )}
            {activeStep === 'agree-implement' && (
              <AgreeImplementStep conflict={conflict} user={user} canEdit={userCanEdit} onUpdate={onUpdate} onReturnToOverview={onReturnToOverview} />
            )}
            {activeStep === 'follow-up' && (
              <FollowUpStep conflict={conflict} user={user} canEdit={userCanEdit} onUpdate={onUpdate} onReturnToOverview={onReturnToOverview} onLogout={onLogout} />
            )}
          </div>
        );
      })()}
    </div>
  );
}

// Mentee Response Card Component

export default ConflictProcess;
